import { Role } from '@seen/core/roles'
import { now } from '@seen/core/utils'
import { Button, TypographyPageTitle } from '@seen/ui'
import { IconCalendarPlus } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { AppointmentsTable } from '#src/components/appointment/AppointmentsTable.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import {
  buildCalendarQueryParam,
  ViewHorizon,
  ViewType,
} from '#src/stores/calendar.ts'

export function PatientAppointmentsPage(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const participant = useCurrentParticipant()
  const practitioner = useCurrentPractitioner()

  const calendarPageQueryParams = useMemo(() => {
    const _now = now()
    return buildCalendarQueryParam({
      requestPatient: participant.reference,
      requestPractitioner: practitioner.isIDT()
        ? practitioner.reference
        : undefined,
      viewType: ViewType.TimeGrid,
      viewHorizon: ViewHorizon.Week,
      windowStart: _now,
      windowEnd: _now,
      showRequestAppointment: true,
      requestRequiresTransportation: false,
      appointmentStatuses: [],
      appointmentFilters: compact([
        practitioner.isIDT() && !practitioner.isRole(Role.CM, Role.TR)
          ? practitioner.reference
          : undefined,
        participant.reference,
      ]),
      aggregateCalendarFilters: [],
    })
  }, [participant, practitioner])

  return (
    <div className="grid gap-6">
      <div className="flex items-center justify-between gap-4">
        <TypographyPageTitle>Appointments</TypographyPageTitle>
        {!isReadOnly && (
          <Button variant="outline" asChild>
            <Link
              to={`/calendar?${calendarPageQueryParams}`}
              className="flex items-center gap-2"
            >
              <IconCalendarPlus size={16} />
              Schedule Appointment
            </Link>
          </Button>
        )}
      </div>
      <AppointmentsTable patientId={participant.id} />
    </div>
  )
}
